import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const OrderColumnsConfigWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  box-sizing: border-box;

  .search-bar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem;
    background: ${(p) => `${p.theme.colors.onSurface}08`};
    border-radius: 0.75rem;
    border: 1px solid ${(p) => `${p.theme.colors.onSurface}15`};
  }

  .search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: ${(p) => p.theme.colors.text};
    font-size: 0.9rem;
    padding: 0.5rem;

    &::placeholder {
      color: ${(p) => p.theme.colors.textLight};
    }
  }

  .default-config-button,
  .toggle-all-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    background: transparent;
    color: ${(p) => p.theme.colors.text};
    border: 1px solid ${(p) => `${p.theme.colors.onSurface}20`};
    border-radius: 0.5rem;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    svg {
      width: 1rem;
      height: 1rem;
    }

    &:hover {
      background: ${(p) => `${p.theme.colors.onSurface}10`};
      border-color: ${(p) => `${p.theme.colors.primary}60`};
    }

    &.active {
      background: ${(p) => p.theme.colors.primary};
      color: ${(p) => p.theme.colors.onPrimary};
      border-color: ${(p) => p.theme.colors.primary};
      
      &:hover {
         background: ${(p) => p.theme.colors.primary};
      }
    }
  }

  .default-config-button {
    align-self: flex-start;
  }

  .search-input {
    min-width: 0;
  }

  .stats-bar {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid ${(p) => `${p.theme.colors.onSurface}15`};
  }

  .stat-badge {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 1rem;
    background: ${(p) => `${p.theme.colors.onSurface}10`};
    color: ${(p) => p.theme.colors.text};

    &.primary {
      background: ${(p) => `${p.theme.colors.primary}15`};
      color: ${(p) => p.theme.colors.primary};
    }

    &.success {
      background: ${(p) => `${p.theme.colors.positive}15`};
      color: ${(p) => p.theme.colors.positive};
    }
  }

  .columns-list {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    max-height: 400px;
    overflow-y: auto;
    padding-right: 0.5rem;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${(p) => `${p.theme.colors.onSurface}30`};
      border-radius: 2px;
    }
  }

  .column-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.625rem 0.875rem;
    background: ${(p) => `${p.theme.colors.onSurface}08`};
    border: 1px solid ${(p) => `${p.theme.colors.onSurface}15`};
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    animation: ${slideIn} 0.2s ease;
    cursor: grab;
    opacity: 0.6;

    &:hover {
      border-color: ${(p) => `${p.theme.colors.primary}40`};
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    &:active {
      cursor: grabbing;
    }

    &.visible {
      background: ${(p) => p.theme.colors.surface};
      opacity: 1;
    }

    &.fixed {
      background: ${(p) => `${p.theme.colors.primary}08`};
      border-color: ${(p) => `${p.theme.colors.primary}25`};
    }
  }

  .column-name {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    font-weight: 500;
    color: ${(p) => p.theme.colors.text};

    .drag-handle {
      display: flex;
      color: ${(p) => p.theme.colors.textLight};
      opacity: 0.5;
      cursor: grab;

      svg {
        width: 1rem;
        height: 1rem;
      }
    }
  }

  .column-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  
  .move-buttons-group {
    display: flex;
    align-items: center;
    margin-left: 0.25rem;
    padding-left: 0.5rem;
    border-left: 1px solid ${(p) => `${p.theme.colors.onSurface}15`};
  }

  .action-icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 0.375rem;
    color: ${(p) => p.theme.colors.textLight};
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
      width: 1.125rem;
      height: 1.125rem;
    }

    &:hover {
      background: ${(p) => `${p.theme.colors.onSurface}10`};
      color: ${(p) => p.theme.colors.primary};
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
    
    /* Variants when active */
    &.active {
       &.primary {
         background: ${(p) => `${p.theme.colors.primary}15`};
         color: ${(p) => p.theme.colors.primary};
         
         &:hover {
             background: ${(p) => `${p.theme.colors.primary}20`};
         }
       }
       
       &.success {
         background: ${(p) => `${p.theme.colors.positive}15`};
         color: ${(p) => p.theme.colors.positive};
         
         &:hover {
             background: ${(p) => `${p.theme.colors.positive}20`};
         }
       }
    }
  }

  .empty-search {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: ${(p) => p.theme.colors.textLight};
    text-align: center;

    svg {
      width: 3rem;
      height: 3rem;
      margin-bottom: 0.75rem;
      opacity: 0.5;
    }

    p {
      font-size: 0.9rem;
      margin: 0;
    }
  }
`;
